import { ApiError, emptyIfForbidden, type Api, type Filter } from "../../../data/api";
import type {
  BuchungsDaten, KennzahlenDaten, KzBooking, KzBrand, KzDeal, KzExtra, KzFx, KzInvoice, KzPeriod, KzSettings, KzTalent, KzTerms,
} from "./model";
import { t } from "../../../lib/sprache";

/**
 * Lader und Schreibwege der Kennzahlen-Seite. Sie bekommen den Datenanschluss übergeben, damit dieselben Funktionen in der App
 * und in den Tests gegen die Demo-Datenbank laufen. RLS begrenzt, was ankommt (eingeschränkte Manager sehen nur zugewiesene
 * Creator); Buchen und Stornieren prüft der Wächter app.guard_booking (Recht „Zahlungen bestätigen“).
 */

const BUCHUNG_SPALTEN =
  "id, org_id, deal_id, period_id, talent_id, brand_id, invoice_id, kind, booked_on, amount_cents, currency, fx_rate_to_org, amount_org_cents, " +
  "talent_share_cents, agency_share_cents, visible_to_talent, status, source, note, created_at, booked_at";

/**
 * Nicht lesbar ergibt eine leere Liste (interne Konditionen, Einstellungen): die Seite zeigt dann, was freigegeben ist. Ein Lade-
 * oder Netzfehler bleibt ein Fehler, damit die Kennzahlen nie still 0 € zeigen (AD-016); die Seite bietet „Erneut versuchen“.
 */
function quiet<T>(p: Promise<T[]>): Promise<T[]> {
  return emptyIfForbidden(p);
}

export async function ladeKennzahlen(api: Api, orgId: string): Promise<KennzahlenDaten> {
  const by: Filter[] = [["org_id", "eq", orgId]];
  const [talents, brands, deals, periods, extras, terms, invoices, bookings, settings, fx, contracts, contractLinks, invoicePeriods, platforms] = await Promise.all([
    api.select<KzTalent>("talents", { columns: "id, first_name, last_name, display_name, status, commission_bp", filters: by, order: [["first_name", "asc"]] }),
    api.select<KzBrand>("brands", { columns: "id, name, invoice_route", filters: by, order: [["name", "asc"]] }),
    // Weg, Staffel, Abzug der Marke und Warenwert-Versteuerung (0039, 0047, 0060)
    api.select<KzDeal>("deals", {
      columns: "id, deal_number, talent_id, brand_id, title, model, status, currency, budget_cents, commission_bp, platforms, archived_at, invoice_route, " +
        "commission_tiers, brand_discount_bp, brand_discount_bearer, products_flat_taxed, origin, post_term, post_term_from, post_term_until", filters: by,
    }),
    api.select<KzPeriod>("deal_periods", {
      columns: "id, deal_id, month, amount_cents, commission_forecast_cents, booked_at, invoice_requested_at, status, outcome, cancellation_fee_cents, goods_value_cents",
      filters: by, order: [["month", "asc"]],
    }),
    api.select<KzExtra>("deal_line_items", { columns: "deal_id, period_id, amount_cents, commissionable, created_at", filters: by }),
    quiet(api.select<KzTerms>("deal_agency_terms", {
      columns: "deal_id, agency_fee_mode, agency_fee_bp, agency_fee_cents, referral_mode, referral_bp, referral_cents, referral_base, referral_recipient", filters: by,
    })),
    api.select<KzInvoice>("invoices", {
      columns: "id, kind, deal_id, period_id, talent_id, brand_id, number, status, currency, gross_cents, net_cents, revenue_month, issue_date, " +
        "document_due_date, effective_due_date, amount_org_cents, created_at, paid_cents, bad_debt_at, sent_at, delivered_at, credit_for",
      filters: by,
    }),
    quiet(api.select<KzBooking>("bookings", { columns: BUCHUNG_SPALTEN, filters: by, order: [["booked_on", "desc"]] })),
    quiet(api.select<KzSettings>("org_settings", {
      columns: "currency, currencies, default_commission_bp, agency_fee_mode, agency_fee_bp, agency_fee_cents, revenue_start, invoice_route, invoice_number_prefix", filters: by,
    })).then((r) => r[0] ?? null),
    quiet(api.select<KzFx>("fx_rates_monthly", { columns: "base, quote, valid_on, rate", order: [["valid_on", "asc"]] })),
    // Umsatzbeginn nach Vertrag und Sammelrechnungen (0047)
    quiet(api.select<{ id: string; deal_id: string | null; status: string }>("contracts", { columns: "id, deal_id, status", filters: by })),
    quiet(api.select<{ deal_id: string; contract_id: string }>("contract_deal_links", { columns: "deal_id, contract_id", filters: by })),
    quiet(api.select<{ invoice_id: string; period_id: string }>("invoice_periods", { columns: "invoice_id, period_id", filters: by })),
    // Katalog der Plattformen der Agentur (Befund 15)
    quiet(api.select<{ key: string; label_de: string; label_en: string | null }>("org_deal_platforms", { columns: "key, label_de, label_en", filters: by })),
  ]);
  return {
    talents, brands, deals, periods, extras, terms, invoices, bookings, settings, fx,
    contracts, contractLinks, contractStatus: contracts.map((c) => ({ id: c.id, status: c.status })), invoicePeriods, platforms,
  };
}

// ── Buchungen schreiben ────────────────────────────────────────────────────

function verweigert(was: string): ApiError {
  return new ApiError(t(`${was} nicht möglich: Die Buchung wurde nicht gefunden oder ist für dein Konto nicht freigegeben.`, `Cannot ${was}: the booking was not found or is not available to your account.`), { code: "42501" });
}

/** Neue manuelle Buchung (geplant, oder sofort gebucht mit dem Recht „Zahlungen bestätigen“). Kurs friert die Datenbank beim Buchen ein. */
export async function legeBuchungAn(api: Api, orgId: string, daten: BuchungsDaten): Promise<KzBooking> {
  const rows = await api.insert<KzBooking>("bookings", { org_id: orgId, source: "manual", ...daten });
  if (!rows[0]) throw verweigert(t("Anlegen", "create"));
  return rows[0];
}

/**
 * Geplante Buchung ändern (alle Felder) bzw. gebuchte nur in der Notiz: gebuchte Beträge sind fest, Korrekturen laufen über
 * eine Gegenbuchung (Wächter). Ein Statuswechsel läuft über setzeBuchungsStatus.
 */
export async function aendereBuchung(api: Api, id: string, daten: Partial<Omit<BuchungsDaten, "status">>): Promise<KzBooking> {
  const rows = await api.update<KzBooking>("bookings", [["id", "eq", id]], { ...daten });
  if (!rows[0]) throw verweigert(t("Ändern", "change"));
  return rows[0];
}

/** Buchen (geplant → gebucht) oder stornieren (gebucht → storniert); beides nur mit dem Recht „Zahlungen bestätigen“. */
export async function setzeBuchungsStatus(api: Api, id: string, status: "booked" | "cancelled", notiz?: string | null): Promise<KzBooking> {
  const patch: Record<string, unknown> = { status };
  if (notiz !== undefined) patch.note = notiz;
  const rows = await api.update<KzBooking>("bookings", [["id", "eq", id]], patch);
  if (!rows[0]) throw verweigert(status === "booked" ? t("Buchen", "Book") : t("Stornieren", "Cancel booking"));
  return rows[0];
}

/** Nur geplante Buchungen lassen sich löschen; gebuchte werden storniert (Wächter). */
export async function loescheBuchung(api: Api, id: string): Promise<void> {
  await api.remove("bookings", [["id", "eq", id]]);
  const rest = await api.select<{ id: string }>("bookings", { columns: "id", filters: [["id", "eq", id]] });
  if (rest.length) throw verweigert(t("Löschen", "delete"));
}
